import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { OverExposedType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { MiddlewareConfig, OverExposedAccount, Rtbs, Sdks } from '../types';
import { Group, GroupWithExtraInfo } from '../types/Group.type';
import { Portfolio, PortfolioWithExtraInfo } from '../types/Portfolio.type';
import { formatOverExposedAccounts, formatPortfolio } from '../utils/dataUtils';
import { IBranchApi } from './interfaces';
import { IGroupApi } from './interfaces/IGroupApi';

@injectable()
export class GroupApi extends CachedResponseApi implements IGroupApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) private branchApi: IBranchApi,
    ) {
        super(sdks, config, cache);
    }

    async getGroups(): Promise<GroupWithExtraInfo[]> {
        const data = await Promise.all([this.sdks.orbis.get('v1/groups/list'), this.branchApi.getAccounts()]);
        const groups = data[0].data as Group[];
        const accounts = data[1];

        return groups.map((group) => {
            const groupAccounts = accounts.filter((account) => group.accountIds.includes(account.accountId));

            const rtbs = groupAccounts.reduce<Rtbs>(
                (result, account) => {
                    result.balance += account.balance;
                    result.cashOnAccount += account.cashOnAccount;
                    result.markToMarket += account.markToMarket;
                    result.sodCashBalance += account.sodCashBalance;
                    result.sodMarginBalance += account.sodMarginBalance;
                    result.sodShortBalance += account.sodShortBalance;
                    result.totalDividendAmount += account.totalDividendAmount;
                    result.totalDividendYield += account.totalDividendYield;
                    return result;
                },
                {
                    balance: 0,
                    cashOnAccount: 0,
                    markToMarket: 0,
                    sodCashBalance: 0,
                    sodMarginBalance: 0,
                    sodShortBalance: 0,
                    totalDividendAmount: 0,
                    totalDividendYield: 0,
                },
            );

            return {
                ...group,
                ...rtbs,
                accounts: groupAccounts,
            };
        });
    }

    async getGroup(groupId: number): Promise<GroupWithExtraInfo | null> {
        const data = await this.getGroups();
        const group = data.find((group) => group.id === groupId);

        if (!group) {
            return null;
        }

        return group;
    }

    async getPortfolio(groupId: number, combined = true, symbol?: string): Promise<PortfolioWithExtraInfo> {
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.getGroup(groupId),
            this.sdks.orbis.get('v2/advisory/group/portfolios', undefined, {
                urlQuery: groupId,
                params: {
                    loadQuotes: true,
                    loadIndustries: true,
                    loadCost: !combined,
                    combined,
                },
            }),
        ]);

        const accounts = data[0];
        const group = data[1];
        const portfolio = data[2].data as Portfolio;

        return formatPortfolio(portfolio, accounts, [], group?.balance || 0, { symbol });
    }

    async getOverExposedAccounts(
        groupId: number,
        threshold: number,
        type: OverExposedType,
    ): Promise<OverExposedAccount[]> {
        const { data } = await this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
            urlQuery: [type, 'group', groupId],
            params: { threshold },
        });
        const accounts = await this.branchApi.getAccounts();

        return formatOverExposedAccounts(data, type, accounts);
    }
}
